import { ref, getCurrentInstance } from 'vue';
import store from '@/store';

const DEFAULT_PAIS_ID = 68;

/**
 * Owns the "provincias by pais" lookup used by the cascading Pais → Provincia
 * control.
 *
 * Why it exists: `Provincias.vue` used to dispatch straight from the component,
 * mixing presentation with store access AND allowing a race — changing the
 * country twice quickly could let the older response land last and leave
 * `provincias` populated with the previous country's list. A request token
 * guarantees only the response matching the current pais is applied.
 *
 * @returns {Object} Reactive state plus the loader.
 */
export function useProvincias() {
    const instance = getCurrentInstance();
    const toast = instance?.proxy?.$toast;

    const provincias = ref([]);
    const loading = ref(false);
    const error = ref(null);

    // Monotonic token: only the response whose token still equals the latest
    // one is applied, so a slow older request cannot overwrite newer data.
    let requestId = 0;

    /**
     * Loads provincias for a pais.
     *
     * @param {number} [pais] pais id; defaults to Spain (68).
     * @returns {Promise<Array>} The applied provincias, or [] if stale/failed.
     */
    const cargarProvincias = async(pais = DEFAULT_PAIS_ID) => {
        requestId += 1;
        const token = requestId;

        loading.value = true;
        error.value = null;

        try {
            const data = {
                filters: { pais_id: pais },
            };
            const response = await store.dispatch(
                store.getters.getConfig?.getVuexAlias?.('select.provincias') || 'SELECT_PROVINCIAS',
                data
            );

            // Stale response: the pais changed while this was in flight.
            if (token !== requestId) {
                return [];
            }

            provincias.value = response.data.data;
            return provincias.value;
        } catch (err) {
            if (token !== requestId) {
                return [];
            }

            error.value = err;
            provincias.value = [];
            toast?.error(err?.message);
            return [];
        } finally {
            if (token === requestId) {
                loading.value = false;
            }
        }
    };

    return {
        provincias,
        loading,
        error,
        cargarProvincias,
    };
}
